<template>
<div class="toolbar-panel">
    <v-item-group class="v-btn-toggle" :light="isFullscreen">
    <v-btn
        v-if="!structureFileButtonDisabled"
        v-bind="toolbarButtonProps"
        @click="$emit('makeCIF'); $emit('makePDB')"
        :title="structureFileButtonLabel"
    >
        <v-icon v-bind="toolbarIconProps">{{ structureFileIcon }}</v-icon>
        <span v-if="isFullscreen">&nbsp;{{ structureFileButtonLabel }}</span>
    </v-btn>
    <v-btn
        v-if="!disableImageButton"
        v-bind="toolbarButtonProps"
        @click="$emit('makeImage')"
        title="Save image"
    >
        <v-icon v-bind="toolbarIconProps">{{ $MDI.SavePNG }}</v-icon>
        <span v-if="isFullscreen">&nbsp;Save image</span>
    </v-btn>
    <v-btn
        v-if="!disableQueryButton"
        v-bind="toolbarButtonProps"
        @click="$emit('toggleQuery')"
        :title="queryToggleTitle"
    >
        <span v-if="queryColorList.length >= 2" class="two-tone-icon">
            <!-- The icon shape itself changes with 
                 `showQuery`: CircleOneThird -> CircleTwoThird -> Circle. -->
            <v-icon v-bind="toolbarIconProps"
                    :style="queryFirstHalfStyle">
                {{ queryIconName }}
            </v-icon>
            <v-icon v-bind="toolbarIconProps"
                    :style="querySecondHalfStyle">
                {{ queryIconName }}
            </v-icon>
            <!-- Invisible sizing icon so the span has intrinsic width/height. -->
            <v-icon v-bind="toolbarIconProps" style="visibility: hidden;">
                {{ queryIconName }}
            </v-icon>
        </span>
        <v-icon v-else v-bind="toolbarIconProps" :style="{ color: queryColorList[0] }">
            {{ queryIconName }}
        </v-icon>
        <span v-if="isFullscreen">&nbsp;Toggle full query</span>
  </v-btn>
    <v-btn
        v-if="!disableTargetButton"
        v-bind="toolbarButtonProps"
        @click="$emit('toggleTarget')"
        :title="targetToggleTitle"
    >
        <span v-if="targetColorList.length >= 2" class="two-tone-icon">
            <v-icon v-bind="toolbarIconProps"
                    :style="targetFirstHalfStyle">
                {{ targetIconName }}
            </v-icon>
            <v-icon v-bind="toolbarIconProps"
                    :style="targetSecondHalfStyle">
                {{ targetIconName }}
            </v-icon>
            <v-icon v-bind="toolbarIconProps" style="visibility: hidden;">
                {{ targetIconName }}
            </v-icon>
        </span>
        <v-icon v-else v-bind="toolbarIconProps" :style="{ color: targetColorList[0] }">
            {{ targetIconName }}
        </v-icon>
        <span v-if="isFullscreen">&nbsp;Toggle full target</span>
    </v-btn>
    <v-btn
        v-if="separationToggle"
        v-bind="toolbarButtonProps"
        @click="$emit('toggle-structure-separation')"
        :title="separateStructures ? 'Overlay query and target structures' : 'Separate query and target structures while preserving their superposition'"
    >
        <v-icon v-bind="toolbarIconProps">
            {{ separateStructures ? $MDI.Layers : $MDI.LayersOutline }}
        </v-icon>
        <span v-if="isFullscreen">&nbsp;{{ separateStructures ? 'Overlay structures' : 'Separate structures' }}</span>
    </v-btn>
    <v-btn
        v-if="!disableArrowButton"
        v-bind="toolbarButtonProps"
        @click="$emit('toggleArrows')"
        title="Draw arrows between aligned residues"
    >
        <v-icon v-bind="toolbarIconProps" v-if="showArrows">{{ $MDI.ArrowRightCircle }}</v-icon>
        <v-icon v-bind="toolbarIconProps" v-else>{{ $MDI.ArrowRightCircleOutline }}</v-icon>
        <span v-if="isFullscreen">&nbsp;Toggle arrows</span>
    </v-btn>
    <v-btn
        v-if="!disableResetButton"
        v-bind="toolbarButtonProps"
        @click="$emit('resetView')"
        title="Reset the view to the original position and zoom level"
    >
        <v-icon v-bind="toolbarIconProps">{{ $MDI.Restore }}</v-icon>
        <span v-if="isFullscreen">&nbsp;Reset view</span>
    </v-btn>
    <v-btn
        v-if="!disableSpinButton"
        v-bind="toolbarButtonProps"
        @click="$emit('toggleSpin')"
        title="Toggle spinning structure"
    >
        <v-icon v-bind="toolbarIconProps">{{ $MDI.AxisZRotateCounterclockwise }}</v-icon>
        <span v-if="isFullscreen">&nbsp;Toggle spin</span>
    </v-btn>
    <v-btn
        v-if="!disableFullscreenButton"
        v-bind="toolbarButtonProps"
        @click="$emit('toggleFullscreen')"
        title="Enter fullscreen mode - press ESC to exit"
    >
        <v-icon v-bind="toolbarIconProps">{{ $MDI.Fullscreen }}</v-icon>
        <span v-if="isFullscreen">&nbsp;Fullscreen</span>
    </v-btn>
    </v-item-group>
</div>
</template>

<script>
export default {
    props: {
        showQuery: { type: Number, default: 0 },
        showTarget: { type: Number, default: 0 },
        showArrows: { type: Boolean, default: false },
        isFullscreen: { type: Boolean, default: false },
        isSpinning: { type: Boolean, default: true },
        disableCIFButton: { default: null },
        disablePDBButton: { type: Boolean, default: false },
        disableSpinButton: { type: Boolean, default: false },
        disableImageButton: { type: Boolean, default: false },
        disableQueryButton: { type: Boolean, default: false },
        disableTargetButton: { type: Boolean, default: false },
        disableArrowButton: { type: Boolean, default: false },
        disableResetButton: { type: Boolean, default: false },
        disableFullscreenButton: { type: Boolean, default: false },
        cifButtonLabel: { type: String, default: null },
        pdbButtonLabel: { type: String, default: 'Save PDB' },
        // Accepts a single color string OR an array of colors.
        // When an array of >=2 colors is passed the icon is rendered as a two-tone split
        queryColors: { type: [String, Array], default: () => "#1E88E5" },
        targetColors: { type: [String, Array], default: () => "#FFC107" },
        // When true the query/target toggles are treated as a binary switch
        // (0 = aligned only, 1 = full) instead of the default 3-state cycle
        // (0 = aligned, 1 = full aligned chains, 2 = full complex incl.
        // non-aligned chains). Icons collapse to CircleHalf / Circle.
        binaryToggle: { type: Boolean, default: false },
        // Interface mode uses the normal three-state cycle, but its states
        // are interface-only, interface plus faded chain context, and solid
        // chains rather than alignment coverage.
        interfaceToggle: { type: Boolean, default: false },
        separationToggle: { type: Boolean, default: false },
        separateStructures: { type: Boolean, default: false },
    },
    computed: {
        structureFileButtonDisabled: function() {
            return this.disableCIFButton !== null ? this.disableCIFButton : this.disablePDBButton;
        },
        structureFileButtonLabel: function() {
            return this.cifButtonLabel || this.pdbButtonLabel;
        },
        structureFileIcon: function() {
            return this.cifButtonLabel ? this.$MDI.SaveCIF : this.$MDI.SavePDB;
        },
        toolbarIconProps: function() {
            return this.isFullscreen ? { right: true } : {};
        },
        toolbarButtonProps: function() {
            return (this.isFullscreen) ? {
                small: false,
            } : {
                small: true,
                style: "width: 24px;",
            }
        },
        queryColorList: function() {
            return Array.isArray(this.queryColors) ? this.queryColors : [this.queryColors];
        },
        targetColorList: function() {
            return Array.isArray(this.targetColors) ? this.targetColors : [this.targetColors];
        },
        queryToggleTitle: function() {
            return this.interfaceToggle
                ? 'Cycle query view: interface residues, faded chain context, solid chains'
                : 'Toggle between the entire query structure and aligned region';
        },
        targetToggleTitle: function() {
            return this.interfaceToggle
                ? 'Cycle target view: interface residues, faded chain context, solid chains'
                : 'Toggle between the entire target structure and aligned region';
        },
        // Two-state icon path: CircleHalf (aligned only) vs Circle (full).
        isBinary: function() {
            return this.$LOCAL || this.binaryToggle;
        },
        queryIconName: function() {
            if (this.isBinary) {
                return this.showQuery === 0 ? this.$MDI.CircleHalf : this.$MDI.Circle;
            }
            if (this.showQuery === 0) return this.$MDI.CircleOneThird;
            if (this.showQuery === 1) return this.$MDI.CircleTwoThird;
            return this.$MDI.Circle;
        },
        targetIconName: function() {
            if (this.isBinary) {
                return this.showTarget === 0 ? this.$MDI.CircleHalf : this.$MDI.Circle;
            }
            if (this.showTarget === 0) return this.$MDI.CircleOneThird;
            if (this.showTarget === 1) return this.$MDI.CircleTwoThird;
            return this.$MDI.Circle;
        },
        queryIsSymmetricIcon: function() {
            return this.queryIconName === this.$MDI.Circle;
        },
        targetIsSymmetricIcon: function() {
            return this.targetIconName === this.$MDI.Circle;
        },
        queryFirstHalfStyle: function() {
            const base = { color: this.queryColorList[0], position: 'absolute' };
            if (this.queryIsSymmetricIcon) base.clipPath = 'inset(0 50% 0 0)';
            return base;
        },
        querySecondHalfStyle: function() {
            const base = { color: this.queryColorList[1], position: 'absolute', mixBlendMode: 'multiply' };
            if (this.queryIsSymmetricIcon) {
                base.clipPath = 'inset(0 0 0 50%)';
            } else {
                base.transform = 'scaleX(-1)';
            }
            return base;
        },
        targetFirstHalfStyle: function() {
            const base = { color: this.targetColorList[0], position: 'absolute' };
            if (this.targetIsSymmetricIcon) base.clipPath = 'inset(0 50% 0 0)';
            return base;
        },
        targetSecondHalfStyle: function() {
            const base = { color: this.targetColorList[1], position: 'absolute', mixBlendMode: 'multiply' };
            if (this.targetIsSymmetricIcon) {
                base.clipPath = 'inset(0 0 0 50%)';
            } else {
                base.transform = 'scaleX(-1)';
            }
            return base;
        },
    },
}
</script>

<style>
.toolbar-panel {
    display: inline-flex;
    flex-direction: row;
    position: absolute;
    justify-content: center;
    width: 100%;
    bottom: 0;
    z-index: 1;
    left: 0;
}
/* Container for the query/target two-tone icons. */
.two-tone-icon {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
}
</style>
